import React from 'react'
import { Box, Typography } from '@mui/material'
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts'
import { INTERAX_COLORS } from '../../../../constants/colors'

export interface PartOfWholeVisualizationProps {
  /**
   * Value representing the filled portion (0-1 normalized)
   */
  value: number
  /**
   * Optional metric name for display
   */
  metricName?: string
}

/**
 * PartOfWholeVisualization Component
 *
 * Displays a part-of-whole relationship using a donut/pie chart.
 * Shows the percentage of a metric relative to the whole.
 *
 * Example use: Constructive disagreement ratio
 *
 * @example
 * ```tsx
 * <PartOfWholeVisualization
 *   value={0.59}
 *   metricName="Constructive Disagreement"
 * />
 * ```
 */
const PartOfWholeVisualization: React.FC<PartOfWholeVisualizationProps> = ({ value, metricName }) => {
  // Convert to percentage
  const percentage = Math.round(value * 100)

  // Prepare chart data
  const chartData = [
    {
      name: 'Filled',
      value: percentage,
      color: INTERAX_COLORS.sun
    },
    {
      name: 'Remaining',
      value: 100 - percentage,
      color: 'rgba(255, 255, 255, 0.1)'
    }
  ]

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        minHeight: '200px',
        position: 'relative'
      }}
    >
      {/* Metric Name (if provided) */}
      {metricName && (
        <Typography
          sx={{
            fontFamily: 'Lexend Deca',
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '20px',
            color: INTERAX_COLORS.white,
            marginBottom: '16px',
            textAlign: 'center'
          }}
        >
          {metricName}
        </Typography>
      )}

      {/* Pie Chart Container */}
      <Box
        sx={{
          width: '200px',
          height: '200px',
          position: 'relative'
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              paddingAngle={0}
              dataKey="value"
              startAngle={90}
              endAngle={450}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        {/* Percentage text in center */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontSize: '32px',
              lineHeight: '40px',
              color: INTERAX_COLORS.white,
              margin: 0,
              textShadow: '0px 0px 10px rgba(0, 0, 0, 0.5)'
            }}
          >
            {percentage}%
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

export default PartOfWholeVisualization
